import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, PillTabs, SectionCard, StatusBadge, formatCompact, formatDate, formatNumber } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type CostLine, type CostProviderId, type CostsResponse } from '../../../common/models/platform-costs.model';
import { money } from './cost-format';

dayjs.extend(relativeTime);

type AzureView = 'service' | 'group';

/** Azure lines are "resource group | service": regroup them by either. */
function regroup(lines: CostLine[], by: AzureView): CostLine[] {
  const map = new Map<string, CostLine>();
  for (const l of lines) {
    const key = by === 'group' ? l.group || '—' : l.label;
    const hit = map.get(key);
    if (hit) hit.usd += l.usd;
    else map.set(key, { key, label: key, usd: l.usd });
  }
  return [...map.values()].sort((a, b) => b.usd - a.usd);
}

function Usage({ line }: { line: CostLine }) {
  const { t } = useTranslation();
  const parts: string[] = [];
  if (line.quantity) {
    const unit = line.unit || '';
    const value = unit === 'tokens' ? formatCompact(line.quantity) : formatNumber(line.quantity, 1);
    const known = ['tokens', 'image', 'images', 'second', 'seconds', 'megapixels'].includes(unit);
    parts.push(t(`ADMIN_COSTS.BREAKDOWN.UNITS.${known ? unit : 'other'}`, { value, unit }));
  }
  if (line.requests) parts.push(t('ADMIN_COSTS.BREAKDOWN.REQUESTS', { count: line.requests }));
  return <span className="text-muted-foreground">{parts.join(' · ') || '—'}</span>;
}

/** What each provider bills for, the OpenRouter keys, and how much of the AI bill our ledger names. */
export function BreakdownCard({ data, className }: { data: CostsResponse; className?: string }) {
  const { t } = useTranslation();
  const withSpend = data.providers.filter((p) => p.status !== 'unconfigured' || p.windowUsd > 0);
  const [tab, setTab] = useState<CostProviderId>(() => (withSpend.sort((a, b) => b.windowUsd - a.windowUsd)[0]?.provider as CostProviderId) || 'openrouter');
  const [azureView, setAzureView] = useState<AzureView>('service');
  const provider = data.providers.find((p) => p.provider === tab);

  const lines = useMemo(() => {
    const raw = data.breakdown[tab] || [];
    return tab === 'azure' ? regroup(raw, azureView) : raw;
  }, [data.breakdown, tab, azureView]);
  const total = lines.reduce((s, l) => s + l.usd, 0) || 1;

  return (
    <SectionCard
      title={t('ADMIN_COSTS.BREAKDOWN.TITLE')}
      description={t('ADMIN_COSTS.BREAKDOWN.DESCRIPTION')}
      className={className}
      testId="costs-breakdown"
      action={
        <PillTabs<CostProviderId>
          value={tab}
          onChange={setTab}
          ariaLabel={t('ADMIN_COSTS.BREAKDOWN.TITLE')}
          options={data.providers.map((p) => ({ value: p.provider, label: PROVIDER_NAMES[p.provider] }))}
        />
      }
    >
      {tab === 'azure' && lines.length > 0 && (
        <PillTabs<AzureView>
          value={azureView}
          onChange={setAzureView}
          className="mb-3"
          options={[
            { value: 'service', label: t('ADMIN_COSTS.BREAKDOWN.BY_SERVICE') },
            { value: 'group', label: t('ADMIN_COSTS.BREAKDOWN.BY_GROUP') },
          ]}
        />
      )}

      {provider?.status === 'unconfigured' && !lines.length ? (
        <EmptyState title={t('ADMIN_COSTS.BREAKDOWN.NOT_CONNECTED', { provider: PROVIDER_NAMES[tab] })} className="py-10" />
      ) : !lines.length ? (
        <EmptyState title={t('ADMIN_COSTS.BREAKDOWN.NONE')} className="py-10" />
      ) : (
        <Table data-testid={`costs-breakdown-${tab}`}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ADMIN_COSTS.BREAKDOWN.LINE')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('ADMIN_COSTS.BREAKDOWN.USAGE')}</TableHead>
              <TableHead className="w-[28%]">{t('ADMIN_COSTS.BREAKDOWN.SHARE')}</TableHead>
              <TableHead className="text-right">{t('ADMIN_COSTS.BREAKDOWN.SPEND')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((l) => (
              <TableRow key={l.key}>
                <TableCell className="max-w-[18rem]">
                  <span className="block truncate font-mono text-[11px]" title={l.label}>
                    {l.label}
                  </span>
                  {l.group && tab !== 'azure' && <span className="block truncate text-[11px] text-muted-foreground">{l.group}</span>}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <Usage line={l} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-foreground/70" style={{ width: `${(l.usd / total) * 100}%` }} />
                    </div>
                    <span className="w-9 text-right text-[11px] text-muted-foreground tabular-nums">{formatNumber((l.usd / total) * 100, 0)}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">{money(l.usd)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {provider?.historyFrom && (
        <p className="mt-3 text-[11px] text-muted-foreground">
          {t('ADMIN_COSTS.BREAKDOWN.HISTORY', { date: formatDate(`${provider.historyFrom}T00:00:00Z`), time: provider.historySyncedAt ? dayjs(provider.historySyncedAt).fromNow() : '—' })}
        </p>
      )}

      {tab === 'openrouter' && provider?.keys?.length ? <KeysTable keys={provider.keys} /> : null}
      {tab === 'openrouter' && provider?.status !== 'unconfigured' ? <LedgerCoverage ledger={data.ledger} /> : null}
    </SectionCard>
  );
}

function KeysTable({ keys }: { keys: NonNullable<CostsResponse['providers'][number]['keys']> }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6" data-testid="costs-keys">
      <p className="text-sm font-medium">{t('ADMIN_COSTS.KEYS.TITLE')}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{t('ADMIN_COSTS.KEYS.DESCRIPTION')}</p>
      <Table className="mt-2">
        <TableHeader>
          <TableRow>
            <TableHead>{t('ADMIN_COSTS.KEYS.KEY')}</TableHead>
            <TableHead className="text-right">{t('ADMIN_COSTS.KEYS.TODAY')}</TableHead>
            <TableHead className="text-right">{t('ADMIN_COSTS.KEYS.MONTH')}</TableHead>
            <TableHead className="hidden text-right md:table-cell">{t('ADMIN_COSTS.KEYS.CAP')}</TableHead>
            <TableHead className="text-right">{t('ADMIN_COSTS.KEYS.LEFT')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {keys.map((k) => (
            <TableRow key={k.key} className={k.disabled ? 'opacity-60' : undefined}>
              <TableCell className="max-w-[16rem]">
                <span className="block truncate">{k.label}</span>
                {k.disabled ? (
                  <StatusBadge tone="neutral">{t('ADMIN_COSTS.KEYS.DISABLED')}</StatusBadge>
                ) : k.limitReset ? (
                  <span className="text-[11px] text-muted-foreground">{t('ADMIN_COSTS.KEYS.RESETS', { period: k.limitReset })}</span>
                ) : null}
              </TableCell>
              <TableCell className="text-right tabular-nums">{money(k.usageTodayUsd)}</TableCell>
              <TableCell className="text-right font-medium tabular-nums">{money(k.usageMonthUsd)}</TableCell>
              <TableCell className="hidden text-right tabular-nums md:table-cell">{k.limitUsd == null ? t('ADMIN_COSTS.KEYS.NO_CAP') : money(k.limitUsd)}</TableCell>
              <TableCell className="text-right tabular-nums">{k.limitRemainingUsd == null ? '—' : money(k.limitRemainingUsd)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LedgerCoverage({ ledger }: { ledger: CostsResponse['ledger'] }) {
  const { t } = useTranslation();
  const total = ledger.providerMonthUsd;
  const named = Math.min(ledger.attributedMonthUsd, total || ledger.attributedMonthUsd);
  const share = total > 0 ? named / total : 1;
  const gap = Math.max(total - ledger.attributedMonthUsd, 0);
  const top = Math.max(...ledger.byFeature.map((f) => f.usd), 0.0001);

  return (
    <div className="mt-6" data-testid="costs-ledger">
      <p className="text-sm font-medium">{t('ADMIN_COSTS.LEDGER.TITLE')}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{t('ADMIN_COSTS.LEDGER.DESCRIPTION')}</p>
      {total <= 0 && !ledger.byFeature.length ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('ADMIN_COSTS.LEDGER.NONE')}</p>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-foreground/70" style={{ width: `${share * 100}%` }} />
            </div>
            <span className="text-xs tabular-nums">{t('ADMIN_COSTS.LEDGER.COVERAGE', { attributed: money(ledger.attributedMonthUsd), total: money(total) })}</span>
          </div>
          {gap >= 0.01 && <p className="mt-2 text-xs text-muted-foreground">{t('ADMIN_COSTS.LEDGER.GAP', { amount: money(gap) })}</p>}
          <ul className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {ledger.byFeature.map((f) => (
              <li key={f.feature} className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-mono">{f.feature}</span>
                  <span className="shrink-0 text-muted-foreground tabular-nums">{money(f.usd)}</span>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-foreground/60" style={{ width: `${(f.usd / top) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
